import React, { CSSProperties, ReactNode } from 'react';
import Modal from 'react-modal';
import { actions, useModal } from '../../context/results-modal-context';
import {
  DateCell,
  OpponentsCell,
  PartnerCell,
  ResultCell,
  RoundPositionCell,
  ScoreCell,
  TeamCell
} from '../resultsTable/tableCells';
import { ReactComponent as CloseIcon } from '../../static/icon-close.svg';
import styles from './resultsTableModal.module.scss';

// this is required for accessibility reasons
const root = document.getElementById('public-teams');

const overlayStyle: CSSProperties = {};

const contentStyle: CSSProperties = {
  position: 'absolute',
  top: '10%',
  left: 0,
  right: 0,
  bottom: 0,
  padding: 0,
  borderRadius: '1rem 1rem 0 0'
};

interface ContentSectionProps {
  title: string;
  content: () => ReactNode;
  classContent?: string;
}

const ContentSection = ({ title, content, classContent = '' }: ContentSectionProps) => {
  return (
    <div className={styles.section}>
      <p className={styles.sectionTitle}>
        <strong>{title}</strong>
      </p>
      <div className={classContent}>{content()}</div>
    </div>
  );
};

interface ResultsTableModalProps {
  type?: string;
  thisPlayer: {
    standardGivenName: string;
    standardFamilyName: string;
  };
}

const ResultsTableModal = ({ type, thisPlayer }: ResultsTableModalProps) => {
  const {
    modalState: { resultData, isOpen },
    dispatch
  } = useModal();

  const closeModal = () => {
    dispatch(actions.closeModal());
  };

  return (
    <Modal
      isOpen={isOpen}
      appElement={root as HTMLElement}
      style={{ overlay: overlayStyle, content: contentStyle }}
      overlayClassName={{
        base: styles.overlay,
        afterOpen: styles.animationOpen,
        beforeClose: styles.animationClose
      }}
      closeTimeoutMS={300}
    >
      <div className={styles.headerContainer}>
        <button
          data-testid="PT-resultsTableModal-onCloseButton"
          className={styles.closeBtn}
          onClick={closeModal}
        >
          <CloseIcon className={styles.closeIcon} />
        </button>
        <p>
          <strong>{`${thisPlayer?.standardGivenName} ${
            thisPlayer?.standardFamilyName ? thisPlayer?.standardFamilyName.toUpperCase() : ''
          }`}</strong>
        </p>
      </div>
      <div className={styles.sectionsContainer}>
        <ContentSection title="Date" content={() => <DateCell value={resultData?.date} />} />
        {type === 'doubles' && (
          <ContentSection
            title="Partner"
            content={() => <PartnerCell value={resultData?.thisPlayers} />}
          />
        )}
        <ContentSection
          title={type === 'doubles' ? 'Opponents' : 'Opponent'}
          content={() => <OpponentsCell value={resultData?.opponentPlayers} />}
          classContent={styles.opponents}
        />
        {resultData?.team.name && (
          <ContentSection
            title="Team"
            content={() => <TeamCell value={resultData?.team} />}
            classContent={styles.team}
          />
        )}

        <ContentSection
          title="W - L"
          content={() => <ResultCell value={resultData?.thisPlayerWon} />}
        />
        <ContentSection title="Score" content={() => <ScoreCell data={resultData} />} />
        <ContentSection
          title="Rnd/Pos"
          content={() => <RoundPositionCell value={resultData?.roundsAndPositions} />}
        />
      </div>
    </Modal>
  );
};

export { ResultsTableModal };
